import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { api } from "../utils/api";
import { useRunStore } from "../state/runStore";
import { CarBadge } from "./CarBadge";

// The Fuel/Pit per-car switcher (#279). It materializes ONLY when a week has 2+
// cars; single-car (custom OR synced) weeks render nothing and the tools keep
// their existing unscoped behavior — no regression.

export interface WeekCar {
  carId: string;
  carName: string | null;
  runCount: number;
  hasPitProfile: boolean;
}

const CAR_LABELS: Record<string, string> = {
  "nascar-cup-next-gen": "Cup · Next Gen",
  "nascar-oreilly": "Xfinity",
  "nascar-trucks": "Trucks",
};

function humanize(slug: string): string {
  return slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function carLabel(c: WeekCar): string {
  return CAR_LABELS[c.carId] || c.carName || humanize(c.carId);
}

/** Fetch a week's cars + the shared (per-week) selected car. selectedCarId is
 *  undefined unless the week has 2+ cars (so single-car tools stay unscoped). */
export function useWeekCars(trackWeekId: string | null | undefined) {
  const selectedCarByWeek = useRunStore((s) => s.selectedCarByWeek);
  const setSelectedCar = useRunStore((s) => s.setSelectedCar);
  const [cars, setCars] = useState<WeekCar[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!trackWeekId) {
      setCars([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api<{ cars: WeekCar[] }>(`/api/track-weeks/${trackWeekId}/cars`)
      .then((d) => !cancelled && setCars(Array.isArray(d.cars) ? d.cars : []))
      .catch(() => !cancelled && setCars([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [trackWeekId]);

  const multi = cars.length > 1;
  const stored = trackWeekId ? selectedCarByWeek[trackWeekId] : undefined;
  const selectedCarId = multi
    ? (stored && cars.some((c) => c.carId === stored) ? stored : cars[0]?.carId)
    : undefined;

  // Persist the default so Fuel and Pit agree on the same car within a week.
  useEffect(() => {
    if (multi && trackWeekId && selectedCarId && stored !== selectedCarId) {
      setSelectedCar(trackWeekId, selectedCarId);
    }
  }, [multi, trackWeekId, selectedCarId, stored, setSelectedCar]);

  const setCar = (id: string) => trackWeekId && setSelectedCar(trackWeekId, id);
  const selected = cars.find((c) => c.carId === selectedCarId) || null;

  return { cars, loading, multi, selectedCarId, selected, setCar };
}

export function CarSwitcherBar({
  cars,
  selectedCarId,
  setCar,
}: {
  cars: WeekCar[];
  selectedCarId: string | undefined;
  setCar: (id: string) => void;
}) {
  if (cars.length <= 1) return null;
  // A CONNECTED segmented control (single-select), visually distinct from the
  // modal's loose multi-select toggle pills. role=radiogroup, not tablist —
  // there is no tabpanel; this is a one-of-N filter.
  return (
    <div style={styles.bar}>
      <span className="section-label" style={{ margin: 0 }}>Car</span>
      <div style={styles.segment} role="radiogroup" aria-label="Car">
        {cars.map((c) => {
          const on = c.carId === selectedCarId;
          return (
            <button
              key={c.carId}
              type="button"
              role="radio"
              aria-checked={on}
              style={{ ...styles.seg, ...(on ? styles.segOn : null) }}
              onClick={() => setCar(c.carId)}
            >
              <CarBadge slug={c.carId} size={18} />
              {carLabel(c)}
              {c.runCount ? <span style={{ ...styles.count, ...(on ? styles.countOn : null) }}> · {c.runCount}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  bar: { display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap", marginBottom: "1rem" },
  segment: {
    display: "inline-flex", padding: "2px", gap: "2px",
    border: "1px solid var(--rule)", borderRadius: "var(--r-md)",
    background: "var(--ink-1)",
  },
  seg: {
    cursor: "pointer", border: "none", background: "transparent",
    color: "var(--text-secondary)", fontFamily: "var(--font-body)",
    fontSize: "0.8125rem", fontWeight: 600, padding: "0.3rem 0.7rem",
    borderRadius: "var(--r-sm)", lineHeight: 1.2,
    display: "inline-flex", alignItems: "center", gap: "0.4rem",
  },
  segOn: { background: "var(--live)", color: "var(--ink-0)" },
  count: { color: "var(--text-faint)", fontWeight: 500 },
  countOn: { color: "var(--ink-0)", opacity: 0.75 },
};
